import React from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import NavigationBarComponent from 'components/NavigationBar';
import { NavigationLink, StyledNavElement } from 'components/NavigationBar/styles';
import paths from 'constants/paths';
import {
  OperationRouteName,
  OperationsRouteDeclaration,
  useOperationDetailsRoutes,
} from 'pages/operationPortal/InquiryDetails/useOperationDetailsRoutes';
import CONFIG from 'shared/featureFlagConfig/configFromAdmin';
import { useConfig } from 'shared/featureFlagConfig/useConfig';
import { getCompanyId } from 'store/inquiryDetails/selectors';
import ArrowLeftIcon from 'theme/components/Icon/ArrowLeftIcon';
import { useNavigationAriaCurrent } from 'utils/hooks/useNavigationAriaCurrent';
import { useTranslations } from 'utils/hooks/useTranslations';
import { hasPermission as hasPermissionCreator } from 'utils/user/conditionals';

import { useFetchPlanningEvaluationProfitability } from '../../../../mittweida/portals/operations/hooks/useFetchPlanningEvaluationProfitability';
import { useGetMarketData } from '../../PlanningEvaluation/SharedComponent/PlanningEvaluation.service';
import CompanySubnavigation from '../CompanyRoutes/CompanySubnavigation';
import RealEstateSubNavigation from '../PlanningEvaluationRoutes/RealEstateSubNavigation';

const AdditionalNav = () => {
  const t = useTranslations();
  return (
    <StyledNavElement>
      <NavigationLink exact to={paths.operation.inquiryList}>
        <ArrowLeftIcon boxSize={6} mr={2} />
        {t('buttons.back')}
      </NavigationLink>
    </StyledNavElement>
  );
};

const MainNavElement = ({ path, name, title }: { path: string; name: string; title: string }) => {
  const ariaCurrent = useNavigationAriaCurrent(path);
  const t = useTranslations();

  return (
    <StyledNavElement key={path}>
      <NavigationLink to={path} data-testid={`${name}-link`} aria-current={ariaCurrent}>
        {t(title)}
      </NavigationLink>
    </StyledNavElement>
  );
};

const OperationNavigationBar = ({ id }: { id: string }) => {
  useGetMarketData(id);
  useFetchPlanningEvaluationProfitability();
  const pathDeclarations = useOperationDetailsRoutes();
  const hasPermission = hasPermissionCreator();
  const companyId = useSelector(getCompanyId);
  const location = useLocation();
  const isMarketDataEnabled = useConfig(CONFIG.IS_MARKET_DATA_ENABLED);
  const isProfitabilityCalculationEnabled = useConfig(CONFIG.IS_PROFITABILITY_CALCULATION_ENABLED);
  const isRealEstateExpertEnabled = useConfig(CONFIG.IS_REAL_ESTATE_EXPERT_ENABLED);
  const isQuickCheckEnabled = useConfig(CONFIG.IS_QUICK_CHECK_ENABLED);
  const isMarketValueNavigationEnabled =
    isMarketDataEnabled ||
    isProfitabilityCalculationEnabled ||
    isRealEstateExpertEnabled ||
    isQuickCheckEnabled;

  const companyNavigation = location.pathname.startsWith(
    paths.operation.inquiryDetails.companyDetails.root(id, companyId),
  );
  const realEstateNavigation = location.pathname.startsWith(
    paths.operation.inquiryDetails.planningEvaluation.root(id),
  );

  const filterMarketValueNavigation = (route: OperationsRouteDeclaration) =>
    route.name === OperationRouteName.PlanningEvaluation ? isMarketValueNavigationEnabled : true;

  const mainNav = pathDeclarations
    .filter((r) => (r.permission ? hasPermission(r.permission) : true))
    .filter(filterMarketValueNavigation)
    .map((route) => {
      const path = route.path.replace(':id', id).replace(':companyId', companyId);
      return <MainNavElement key={path} path={path} name={route.name} title={route.title} />;
    });

  return (
    <>
      <NavigationBarComponent mainNavigation={mainNav} additionalNavigation={<AdditionalNav />} />
      {companyNavigation && <CompanySubnavigation inquiryId={id} companyId={companyId} />}
      {realEstateNavigation && <RealEstateSubNavigation />}
    </>
  );
};

export default OperationNavigationBar;
